import React, { useState, useEffect } from 'react';
import { 
  Inbox, 
  Star, 
  StarBorder, 
  Delete, 
  Reply, 
  Forward, 
  Archive, 
  MarkEmailRead, 
  MarkEmailUnread,
  Search,
  FilterList,
  MoreVert,
  AttachFile,
  Schedule,
  Person,
  Business
} from '@mui/icons-material';

const InboxDashboard = () => {
  const [emails, setEmails] = useState([]);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    // Mock data for RM360 inbox
    const mockEmails = [
      {
        id: 1,
        from: 'Khaled Al-Maani',
        fromEmail: 'khaled.almaani@email.com',
        subject: 'Portfolio Review Request - Urgent',
        preview: 'Hi Al, I need to schedule a portfolio review meeting as soon as possible. My investment goals have changed...',
        timestamp: '2 hours ago',
        isRead: false,
        isStarred: true,
        priority: 'high',
        category: 'portfolio',
        attachments: 2,
        customerId: '101136',
        customerSegment: 'Premium'
      },
      {
        id: 2,
        from: 'Nader Al-Masri',
        fromEmail: 'nader.almasri@email.com',
        subject: 'Investment Opportunity Discussion',
        preview: 'Al, I\'ve been researching some new investment opportunities and would like to discuss...',
        timestamp: '4 hours ago',
        isRead: true,
        isStarred: false,
        priority: 'medium',
        category: 'investment',
        attachments: 1,
        customerId: '101982',
        customerSegment: 'Priority'
      },
      {
        id: 3,
        from: 'Mahmoud Al-Jundi',
        fromEmail: 'mahmoud.aljundi@email.com',
        subject: 'Insurance Package Inquiry',
        preview: 'Hello Al, I\'m looking to upgrade my insurance coverage and would appreciate your advice...',
        timestamp: '6 hours ago',
        isRead: true,
        isStarred: false,
        priority: 'medium',
        category: 'insurance',
        attachments: 0,
        customerId: '102874',
        customerSegment: 'Premium'
      },
      {
        id: 4,
        from: 'Amira Al-Kurdi',
        fromEmail: 'amira.alkurdi@email.com',
        subject: 'Credit Review Application',
        preview: 'Hi Al, I\'ve submitted my credit review application and would like to follow up...',
        timestamp: '1 day ago',
        isRead: false,
        isStarred: true,
        priority: 'high',
        category: 'credit',
        attachments: 3,
        customerId: '103926',
        customerSegment: 'General Mass Market'
      },
      {
        id: 5,
        from: 'Yousef Al-Zoubi',
        fromEmail: 'yousef.alzoubi@email.com',
        subject: 'Investment Portfolio Performance',
        preview: 'Al, I\'m concerned about my portfolio performance this quarter. Can we schedule a call...',
        timestamp: '1 day ago',
        isRead: true,
        isStarred: false,
        priority: 'medium',
        category: 'portfolio',
        attachments: 1,
        customerId: '101915',
        customerSegment: 'Premium'
      },
      {
        id: 6,
        from: 'Saja Al-Dabbas',
        fromEmail: 'saja.aldabbas@email.com',
        subject: 'New Product Information Request',
        preview: 'Hello Al, I\'m interested in learning more about your new investment products...',
        timestamp: '2 days ago',
        isRead: true,
        isStarred: false,
        priority: 'low',
        category: 'product',
        attachments: 0,
        customerId: '104567',
        customerSegment: 'Priority'
      },
      {
        id: 7,
        from: 'Mona Ibrahim',
        fromEmail: 'mona.ibrahim@email.com',
        subject: 'Account Statement Query',
        preview: 'Hi Al, I have a question about my recent account statement. There seems to be...',
        timestamp: '2 days ago',
        isRead: true,
        isStarred: false,
        priority: 'low',
        category: 'account',
        attachments: 0,
        customerId: '104789',
        customerSegment: 'Employee Banking'
      },
      {
        id: 8,
        from: 'Leen Abu Zaid',
        fromEmail: 'leen.abuzaid@email.com',
        subject: 'Meeting Reschedule Request',
        preview: 'Al, I need to reschedule our meeting tomorrow. Something urgent came up...',
        timestamp: '3 days ago',
        isRead: false,
        isStarred: false,
        priority: 'medium',
        category: 'meeting',
        attachments: 0,
        customerId: '104890',
        customerSegment: 'Priority'
      }
    ];

    setEmails(mockEmails);
  }, []);

  const handleEmailSelect = (emailId) => {
    setSelectedEmails(prev => 
      prev.includes(emailId) 
        ? prev.filter(id => id !== emailId)
        : [...prev, emailId]
    );
  };

  const handleSelectAll = () => {
    if (selectedEmails.length === emails.length) {
      setSelectedEmails([]);
    } else {
      setEmails(emails.map(email => email.id));
    }
  };

  const handleStarEmail = (emailId) => {
    setEmails(prev => 
      prev.map(email => 
        email.id === emailId 
          ? { ...email, isStarred: !email.isStarred }
          : email
      )
    );
  };

  const handleMarkAsRead = (emailId) => {
    setEmails(prev => 
      prev.map(email => 
        email.id === emailId 
          ? { ...email, isRead: true }
          : email
      )
    );
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'border-l-[#FF6B6B]';
      case 'medium':
        return 'border-l-[#B4E50D]';
      case 'low':
        return 'border-l-[#808080]';
      default:
        return 'border-l-[#2A2A2A]';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'portfolio':
        return <Business className="text-[#B4E50D]" />;
      case 'investment':
        return <Schedule className="text-[#B4E50D]" />;
      case 'insurance':
        return <Business className="text-[#B4E50D]" />;
      case 'credit':
        return <Person className="text-[#FF6B6B]" />;
      case 'product':
        return <Business className="text-[#B4E50D]" />;
      case 'account':
        return <Person className="text-[#B0B0B0]" />;
      case 'meeting':
        return <Schedule className="text-[#B4E50D]" />;
      default:
        return <Person className="text-[#B0B0B0]" />;
    }
  };

  const filteredEmails = emails.filter(email => {
    const matchesSearch = email.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         email.from.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         email.preview.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filter === 'all' || email.category === filter;
    return matchesSearch && matchesFilter;
  });

  const totalPages = Math.ceil(filteredEmails.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentEmails = filteredEmails.slice(startIndex, endIndex);

  const unreadCount = emails.filter(email => !email.isRead).length;
  const starredCount = emails.filter(email => email.isStarred).length;

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center space-y-4 lg:space-y-0">
        <div>
          <h1 className="text-3xl font-bold text-[#FFFFFF]">Inbox</h1>
          <p className="text-[#B0B0B0] mt-1">Manage your customer communications and inquiries</p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="text-right">
            <div className="text-sm text-[#B0B0B0]">Unread Messages</div>
            <div className="text-2xl font-bold text-[#B4E50D]">{unreadCount}</div>
          </div>
          <div className="text-right">
            <div className="text-sm text-[#B0B0B0]">Starred</div>
            <div className="text-2xl font-bold text-[#B4E50D]">{starredCount}</div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-[#1A1A1A] rounded-xl p-6 border border-[#2A2A2A]">
        <div className="flex flex-col lg:flex-row lg:items-center space-y-4 lg:space-y-0 lg:space-x-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 text-[#B0B0B0]" />
            <input
              type="text"
              placeholder="Search emails..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-[#2A2A2A] border border-[#3A3A3A] rounded-xl text-[#FFFFFF] placeholder-[#B0B0B0] focus:outline-none focus:border-[#B4E50D] focus:ring-2 focus:ring-[#B4E50D] focus:ring-opacity-20 transition-all duration-200"
            />
          </div>
          
          <div className="flex items-center space-x-3">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-4 py-3 bg-[#2A2A2A] border border-[#3A3A3A] rounded-xl text-[#FFFFFF] focus:outline-none focus:border-[#B4E50D] focus:ring-2 focus:ring-[#B4E50D] focus:ring-opacity-20 transition-all duration-200"
            >
              <option value="all">All Categories</option>
              <option value="portfolio">Portfolio</option>
              <option value="investment">Investment</option>
              <option value="insurance">Insurance</option>
              <option value="credit">Credit</option>
              <option value="product">Product</option>
              <option value="account">Account</option>
              <option value="meeting">Meeting</option>
            </select>
            
            <button className="p-3 bg-[#2A2A2A] hover:bg-[#3A3A3A] rounded-xl text-[#B0B0B0] hover:text-[#B4E50D] transition-all duration-200">
              <FilterList />
            </button>
          </div>
        </div>
      </div>

      {/* Email List */}
      <div className="bg-[#1A1A1A] rounded-xl border border-[#2A2A2A] overflow-hidden">
        {/* Email List Header */}
        <div className="bg-[#2A2A2A] px-6 py-4 border-b border-[#3A3A3A]">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <input
                type="checkbox"
                checked={selectedEmails.length === emails.length}
                onChange={handleSelectAll}
                className="w-4 h-4 text-[#B4E50D] bg-[#2A2A2A] border-[#3A3A3A] rounded focus:ring-[#B4E50D] focus:ring-2"
              />
              <span className="text-sm text-[#B0B0B0]">
                {selectedEmails.length > 0 ? `${selectedEmails.length} selected` : 'Select all'}
              </span>
            </div>
            
            {selectedEmails.length > 0 && (
              <div className="flex items-center space-x-2">
                <button className="p-2 bg-[#B4E50D] hover:bg-[#B4E50D] rounded-lg text-[#0A0A0A] transition-colors">
                  <MarkEmailRead />
                </button>
                <button className="p-2 bg-[#2A2A2A] hover:bg-[#3A3A3A] rounded-lg text-[#B0B0B0] hover:text-[#FF6B6B] transition-colors">
                  <Delete />
                </button>
                <button className="p-2 bg-[#2A2A2A] hover:bg-[#3A3A3A] rounded-lg text-[#B0B0B0] hover:text-[#B4E50D] transition-colors">
                  <Archive />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Email Items */}
        <div className="divide-y divide-[#2A2A2A]">
          {currentEmails.map((email) => (
            <div 
              key={email.id}
              className={`px-6 py-4 hover:bg-[#2A2A2A] transition-all duration-200 cursor-pointer border-l-4 ${getPriorityColor(email.priority)} ${
                !email.isRead ? 'bg-[#1A1A1A]' : 'bg-[#1A1A1A]'
              }`}
              onClick={() => handleMarkAsRead(email.id)}
            >
              <div className="flex items-start space-x-4">
                <input
                  type="checkbox"
                  checked={selectedEmails.includes(email.id)}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleEmailSelect(email.id);
                  }}
                  className="w-4 h-4 text-[#B4E50D] bg-[#2A2A2A] border-[#3A3A3A] rounded focus:ring-[#B4E50D] focus:ring-2 mt-1"
                />
                
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStarEmail(email.id);
                  }}
                  className="text-[#B0B0B0] hover:text-[#B4E50D] transition-colors"
                >
                  {email.isStarred ? <Star className="text-[#B4E50D]" /> : <StarBorder />}
                </button>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-2">
                        {getCategoryIcon(email.category)}
                        <span className="text-sm text-[#808080]">{email.category}</span>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        email.priority === 'high' ? 'bg-[#FF6B6B] text-[#FFFFFF]' :
                        email.priority === 'medium' ? 'bg-[#B4E50D] text-[#0A0A0A]' :
                        'bg-[#808080] text-[#FFFFFF]'
                      }`}>
                        {email.priority}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2 text-xs text-[#B0B0B0]">
                      {email.attachments > 0 && (
                        <div className="flex items-center space-x-1">
                          <AttachFile className="w-3 h-3" />
                          <span>{email.attachments}</span>
                        </div>
                      )}
                      <span>{email.timestamp}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3 mb-1">
                        <span className={`font-medium ${!email.isRead ? 'text-[#FFFFFF]' : 'text-[#B0B0B0]'}`}>
                          {email.from}
                        </span>
                        <span className="text-xs text-[#808080]">({email.customerSegment})</span>
                      </div>
                      <h3 className={`font-medium mb-1 ${!email.isRead ? 'text-[#FFFFFF]' : 'text-[#B0B0B0]'}`}>
                        {email.subject}
                      </h3>
                      <p className="text-sm text-[#B0B0B0] truncate">
                        {email.preview}
                      </p>
                    </div>
                    
                    <div className="flex items-center space-x-1 ml-4">
                      <button className="p-1 hover:bg-[#2A2A2A] rounded text-[#B0B0B0] hover:text-[#B4E50D] transition-colors">
                        <Reply />
                      </button>
                      <button className="p-1 hover:bg-[#2A2A2A] rounded text-[#B0B0B0] hover:text-[#B4E50D] transition-colors">
                        <Forward />
                      </button>
                      <button className="p-1 hover:bg-[#2A2A2A] rounded text-[#B0B0B0] hover:text-[#B4E50D] transition-colors">
                        <MoreVert />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 bg-[#2A2A2A] border-t border-[#3A3A3A]">
            <div className="flex items-center justify-between">
              <div className="text-sm text-[#B0B0B0]">
                Showing {startIndex + 1}-{Math.min(endIndex, filteredEmails.length)} of {filteredEmails.length} emails
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 bg-[#2A2A2A] text-[#FFFFFF] rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#3A3A3A] transition-colors"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 bg-[#2A2A2A] text-[#FFFFFF] rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#3A3A3A] transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InboxDashboard;
